/**
 * Client-side bracket scoring module.
 *
 * Calculates points for a bracket entry against resolved series.
 * Uses flat NBA-official scoring (no odds-based multipliers).
 *
 * Scoring rules for ALL bracket picks:
 * - Correct series winner: +200 points
 * - Correct winner AND correct exact games score: +200 bonus (total 400)
 * - Incorrect winner: 0 points (even if games count matches)
 *
 * All rounds require score predictions in the Global Bracket.
 * Only completed series contribute to scores.
 * Series that do not yet exist in the DB are silently skipped.
 */

import { BracketEntry, GlobalLeaderboardEntry } from '@/types/bracket';

interface Series {
  id: string;
  status: string | null;
  winner: string | null;
  final_score: string | null;
  round: string | null;
  team_home: string | null;
  team_away: string | null;
}

/**
 * Parse a score string like "4-0" or "Thunder 4-0" into a numeric tuple.
 * Tolerates optional team prefix and whitespace. Returns null on failure.
 */
function parseScoreTuple(score: string | null): [number, number] | null {
  if (!score) return null;
  const m = score.trim().match(/(\d+)\s*-\s*(\d+)\s*$/);
  if (!m) return null;
  const a = Number.parseInt(m[1], 10);
  const b = Number.parseInt(m[2], 10);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return [a, b];
}

function namesEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Compare a winner-perspective predicted_score (e.g. "4-1" or "Thunder 4-0")
 * to a home-away final_score (e.g. "4-0"). Bracket picks store winner-first
 * with no team prefix; bets store winner-first with prefix. Both work.
 */
function isExactScoreMatch(
  predictedScore: string | null,
  finalScore: string | null,
  winner: string | null,
  teamHome: string | null,
): boolean {
  const predicted = parseScoreTuple(predictedScore);
  const final = parseScoreTuple(finalScore);
  if (!predicted || !final || !winner || !teamHome) return false;

  const winnerIsHome = namesEqual(winner, teamHome);
  const finalWinnerFirst: [number, number] = winnerIsHome
    ? [final[0], final[1]]
    : [final[1], final[0]];

  return predicted[0] === finalWinnerFirst[0] && predicted[1] === finalWinnerFirst[1];
}

/** Flat points for predicting the correct series winner */
const WINNER_POINTS = 200;
/** Bonus points for also predicting the exact games score */
const EXACT_SCORE_BONUS = 200;

export interface BracketScore {
  total_points: number;
  correct_winners: number;
  correct_scores: number;
}

/**
 * Calculates bracket score for a single entry against a list of series.
 *
 * For each pick in entry.picks:
 * - Find the matching series by ID
 * - Only score picks where a matching series exists AND is 'completed'
 * - Award WINNER_POINTS (200) for correct winner
 * - Award EXACT_SCORE_BONUS (200) for correct winner + correct score
 * - Gracefully skip picks for series not yet in the DB
 */
export function calculateBracketScore(
  entry: BracketEntry,
  seriesList: Series[],
): BracketScore {
  const completedSeries = seriesList.filter(s => s.status === 'completed');
  const seriesById = new Map(completedSeries.map(s => [s.id, s]));

  let total_points = 0;
  let correct_winners = 0;
  let correct_scores = 0;

  for (const [slotId, pick] of Object.entries(entry.picks)) {
    if (!pick.winner) continue;

    const series = seriesById.get(slotId);

    // Skip if no matching completed series found — expected for future rounds
    if (!series) continue;

    // Incorrect winner = 0 points
    if (!namesEqual(pick.winner, series.winner)) continue;

    // Correct winner: +200
    correct_winners += 1;
    total_points += WINNER_POINTS;

    // Correct exact score: +200 bonus
    if (isExactScoreMatch(pick.predicted_score, series.final_score, series.winner, series.team_home)) {
      correct_scores += 1;
      total_points += EXACT_SCORE_BONUS;
    }
  }

  return { total_points, correct_winners, correct_scores };
}

/**
 * Builds a global leaderboard by scoring all bracket entries against resolved series.
 *
 * - Calculates score per entry using calculateBracketScore
 * - Sorts by total_points DESC, then locked_at ASC (earliest submission wins ties)
 * - Assigns sequential 1-based rank
 * - Uses entry.display_name directly (denormalized at save time)
 * - Falls back to 'שחקן' if display_name is empty
 */
export function buildGlobalLeaderboard(
  entries: BracketEntry[],
  seriesList: Series[],
): GlobalLeaderboardEntry[] {
  const scored = entries.map(entry => {
    const score = calculateBracketScore(entry, seriesList);
    return {
      id: entry.id,
      user_id: entry.user_id,
      display_name: entry.display_name?.trim() || 'שחקן',
      total_points: score.total_points,
      correct_winners: score.correct_winners,
      correct_scores: score.correct_scores,
      rank: 0,
      updated_at: entry.updated_at,
      locked_at: entry.locked_at,
    };
  });

  // Sort by total_points DESC, then locked_at ASC (earliest submission wins ties)
  scored.sort((a, b) => {
    if (b.total_points !== a.total_points) return b.total_points - a.total_points;
    // Tie-breaker: earlier locked_at wins. Null locked_at sorts last.
    const aTime = a.locked_at ? new Date(a.locked_at).getTime() : Infinity;
    const bTime = b.locked_at ? new Date(b.locked_at).getTime() : Infinity;
    return aTime - bTime;
  });

  // Assign rank (1-based, sequential)
  scored.forEach((entry, i) => {
    entry.rank = i + 1;
  });

  // Strip locked_at before returning (not part of GlobalLeaderboardEntry)
  return scored.map(({ locked_at, ...rest }) => rest);
}
